import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { selectorOptions, recommendations } from "../data/recommendations";
import { productById } from "../data/products";
import { AccessCard } from "./AccessCard";

export const CoachSelector = ({ onOpen, onPlay }) => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  const recs = active ? recommendations[active] || [] : [];
  const activeOption = selectorOptions.find((o) => o.key === active);

  return (
    <section id="help" className="relative py-24 md:py-32 px-5 md:px-10 lg:px-16" data-testid="coach-selector-section">
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-14 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#A9824A] mb-3">
            · 07 / Selector
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#F5F1EA] leading-[0.98]">
            {t("selTitle")}
          </h2>
          <p className="mt-5 text-[15px] text-[#E6E0D6]/80 leading-relaxed">{t("selSub")}</p>
        </div>

        {/* Options grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-12">
          {selectorOptions.map((opt) => {
            const isActive = active === opt.key;
            return (
              <motion.button
                key={opt.key}
                onClick={() => setActive(opt.key)}
                whileHover={{ y: -1.5 }}
                whileTap={{ scale: 0.99 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className={`text-start p-5 rounded-2xl border transition-all duration-300 ${
                  isActive
                    ? "border-[#D7B56D] bg-[#D7B56D]/8"
                    : "border-[#D8DCE2]/10 bg-[#15171c] hover:border-[#D7B56D]/35"
                }`}
                data-testid={`selector-opt-${opt.key}`}
              >
                <div className={`font-mono text-[9px] uppercase tracking-[0.22em] mb-2 ${isActive ? "text-[#D7B56D]" : "text-[#A9824A]"}`}>
                  Path · {String(selectorOptions.indexOf(opt) + 1).padStart(2, "0")}
                </div>
                <div className={`font-display text-[14px] font-bold leading-snug ${isActive ? "text-[#F5F1EA]" : "text-[#E6E0D6]"}`}>
                  {t(opt.labelKey)}
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Result */}
        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl glass-strong p-8 md:p-10"
              data-testid="selector-result"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D7B56D] text-[#0a0b0f] font-mono text-[9px] uppercase tracking-[0.22em] font-bold w-fit">
                  <Sparkles size={11} /> {t("selRecommended")}
                </div>
                <div className="font-display text-lg font-bold text-[#F5F1EA]">
                  {t(activeOption.labelKey)}
                </div>
              </div>
              <p className="text-[14px] text-[#E6E0D6]/85 leading-relaxed mb-8 max-w-3xl">
                {t(activeOption.resultKey)}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {recs.slice(0, 3).map((id, i) => {
                  const product = productById(id);
                  return product ? <AccessCard key={id} product={product} index={i} onOpen={onOpen} onPlay={onPlay} /> : null;
                })}
              </div>

              {recs.length > 3 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A9824A] py-2">Also fits:</span>
                  {recs.slice(3).map((id) => {
                    const product = productById(id);
                    return product ? (
                      <button
                        key={id}
                        onClick={() => onOpen(id)}
                        className="tag tag-cyan hover:!border-[#76D9E8]"
                        data-testid={`selector-extra-${id}`}
                      >
                        {product.name} <ArrowRight size={10} className="inline" />
                      </button>
                    ) : null;
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
